import { supabase } from "@/integrations/supabase/client";
import type { Candidate, NewCandidateFormData, UpdateCandidateData } from "@/components/candidates/types";
import { mockCandidates } from "@/utils/mock/candidates";
import { v4 as uuidv4 } from 'uuid';
import { validateStatus, isValidUuid } from '@/utils/candidateUtils';

export async function fetchCandidatesFromDBInternal(): Promise<Candidate[]> {
  try {
    let { data, error } = await supabase
      .from("candidates")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching candidates from Supabase:", error);
      throw error;
    }

    if (!data || data.length === 0) {
      console.log("No candidates found in database. Attempting to seed with mock data.");
      const userResponse = await supabase.auth.getUser();
      const currentUserId = userResponse.data.user?.id;

      if (!currentUserId) {
        console.warn("No authenticated user found. Cannot seed mock data that requires user_id. Returning local mock data; updates will not persist.");
        return mockCandidates.map(candidate => ({
          ...candidate,
          id: isValidUuid(candidate.id) ? candidate.id : uuidv4(),
          status: validateStatus(candidate.status),
          user_id: undefined,
        }));
      }

      const candidatesToInsert = mockCandidates.map(mc => {
        const newId = isValidUuid(mc.id) ? mc.id : uuidv4();
        return {
          id: newId,
          user_id: currentUserId,
          first_name: mc.first_name,
          last_name: mc.last_name,
          email: mc.email,
          phone: mc.phone,
          source: mc.source,
          status: validateStatus(mc.status),
          linkedin_url: mc.linkedin_url,
          experience_years: mc.experience_years,
          notes: mc.notes,
          skills: mc.skills,
          resume_url: mc.resume_url,
          last_contact: mc.last_contact,
          company_rate: mc.placement_fee || null,
        };
      });

      console.log("Inserting mock candidates into DB:", candidatesToInsert);
      const { data: insertedData, error: insertError } = await supabase
        .from("candidates")
        .insert(candidatesToInsert)
        .select();

      if (insertError) {
        console.error("Error inserting mock candidates into Supabase:", insertError);
        console.warn("Falling back to local mock data display due to insert error. Updates will not persist.");
        return mockCandidates.map(candidate => ({
          ...candidate,
          id: isValidUuid(candidate.id) ? candidate.id : uuidv4(),
          status: validateStatus(candidate.status),
          user_id: currentUserId,
        }));
      }
      
      console.log("Mock data successfully inserted. Using inserted data.");
      data = insertedData;
    }
    
    return data.map(dbCandidate => {
      const typedCandidate: Candidate = {
        id: dbCandidate.id,
        first_name: dbCandidate.first_name,
        last_name: dbCandidate.last_name,
        email: dbCandidate.email,
        phone: dbCandidate.phone,
        source: dbCandidate.source,
        status: validateStatus(dbCandidate.status),
        linkedin_url: dbCandidate.linkedin_url,
        experience_years: dbCandidate.experience_years,
        notes: dbCandidate.notes,
        created_at: dbCandidate.created_at,
        updated_at: dbCandidate.updated_at,
        last_contact: dbCandidate.last_contact,
        resume_url: dbCandidate.resume_url,
        skills: dbCandidate.skills,
        company_rate: dbCandidate.company_rate || null,
        user_id: dbCandidate.user_id,
        accepted_date: (dbCandidate as any).accepted_date || null,
        start_date: (dbCandidate as any).start_date || null,
        contract_length: (dbCandidate as any).contract_length || null,
        guarantee_end_date: (dbCandidate as any).guarantee_end_date || null,
      };
      return typedCandidate;
    });
  } catch (error) {
    console.warn("Critical error in fetchCandidatesFromDBInternal, falling back to local mock data display:", error);
    return mockCandidates.map(candidate => ({
      ...candidate,
      id: isValidUuid(candidate.id) ? candidate.id : uuidv4(),
      status: validateStatus(candidate.status),
      user_id: undefined,
    }));
  }
}

export async function createCandidateInDbInternal(candidateData: NewCandidateFormData): Promise<Candidate> {
  const userResponse = await supabase.auth.getUser();
  const currentUserId = userResponse.data.user?.id;

  if (!currentUserId) {
    console.error("No authenticated user found. Cannot create candidate.");
    throw new Error("User must be authenticated to create a candidate.");
  }

  const newCandidateId = uuidv4();
  const candidateToInsert = {
    ...candidateData,
    id: newCandidateId,
    user_id: currentUserId,
    status: validateStatus(candidateData.status || 'new'), // Ensure status is validated or defaults
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from("candidates")
    .insert(candidateToInsert)
    .select()
    .single(); // Assuming insert returns the created row

  if (error) {
    console.error("Error creating candidate in Supabase:", error);
    throw error;
  }

  if (!data) {
    console.error("No data returned after creating candidate.");
    throw new Error("Failed to create candidate: No data returned.");
  }
  
  // Map to Candidate type, ensuring all required fields are present
  const typedCandidate: Candidate = {
    id: data.id,
    user_id: data.user_id,
    first_name: data.first_name,
    last_name: data.last_name,
    email: data.email,
    phone: data.phone,
    source: data.source,
    status: validateStatus(data.status),
    linkedin_url: data.linkedin_url,
    experience_years: data.experience_years,
    notes: data.notes,
    skills: data.skills,
    resume_url: data.resume_url,
    company_rate: data.company_rate,
    created_at: data.created_at,
    updated_at: data.updated_at,
    last_contact: data.last_contact,
  };

  return typedCandidate;
}

export async function updateCandidateStatusInDbInternal(
  { candidateId, status }: { candidateId: string; status: string }
): Promise<Candidate | null> {
  const { data, error } = await supabase
    .from('candidates')
    .update({ status: validateStatus(status), updated_at: new Date().toISOString() })
    .eq('id', candidateId)
    .select(); 

  if (error) {
    console.error('Error updating candidate status in DB:', error);
    throw error;
  }

  if (!data || data.length === 0) {
    console.warn(`Update affected 0 rows: Candidate with ID ${candidateId} not found in database.`);
    throw new Error(`Candidate with ID ${candidateId} not found in the database. Update failed.`);
  }
  
  if (data.length > 1) {
    console.error(`Data integrity issue: Multiple candidates found with ID ${candidateId} after update.`);
    throw new Error(`Multiple candidates found with ID ${candidateId}. Contact support.`);
  }

  const updatedDbCandidate = data[0];
  const typedCandidate: Candidate = {
    id: updatedDbCandidate.id,
    first_name: updatedDbCandidate.first_name,
    last_name: updatedDbCandidate.last_name,
    email: updatedDbCandidate.email,
    phone: updatedDbCandidate.phone,
    source: updatedDbCandidate.source,
    status: validateStatus(updatedDbCandidate.status),
    linkedin_url: updatedDbCandidate.linkedin_url,
    experience_years: updatedDbCandidate.experience_years,
    notes: updatedDbCandidate.notes,
    created_at: updatedDbCandidate.created_at,
    updated_at: updatedDbCandidate.updated_at,
    last_contact: updatedDbCandidate.last_contact,
    resume_url: updatedDbCandidate.resume_url,
    skills: updatedDbCandidate.skills,
    company_rate: updatedDbCandidate.company_rate || null,
    user_id: updatedDbCandidate.user_id,
  };
  return typedCandidate;
}

export async function updateCandidateProfileInDbInternal(
  candidateId: string,
  updateData: UpdateCandidateData
): Promise<Candidate | null> {
  const dataToUpdate = {
    ...updateData,
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('candidates')
    .update(dataToUpdate)
    .eq('id', candidateId)
    .select()
    .single();

  if (error) {
    console.error('Error updating candidate profile in DB:', error);
    throw error;
  }

  if (!data) {
    console.warn(`Update affected 0 rows or no data returned: Candidate with ID ${candidateId}.`);
    // Depending on strictness, you might throw an error or return null
    // For now, returning null if no data, which useMutation can handle in onSuccess if (updatedCandidate)
    return null; 
  }
  
  // Map to Candidate type
  const typedCandidate: Candidate = {
    id: data.id,
    user_id: data.user_id,
    first_name: data.first_name,
    last_name: data.last_name,
    email: data.email,
    phone: data.phone,
    source: data.source,
    status: validateStatus(data.status),
    linkedin_url: data.linkedin_url,
    experience_years: data.experience_years,
    notes: data.notes,
    skills: data.skills,
    resume_url: data.resume_url,
    company_rate: data.company_rate,
    created_at: data.created_at,
    updated_at: data.updated_at,
    last_contact: data.last_contact,
  };

  return typedCandidate;
}
